import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";

export const CREATIVE_VARIANTS = [
  "/creative/archive",
  "/creative/terminal",
  "/creative/constellation",
  "/creative/panorama",
  "/creative/tarot",
  "/creative/broadsheet",
  "/creative/retro",
  "/creative/chronicle",
  "/creative/dashboard",
  "/creative/jukebox",
  "/creative/corkboard",
  "/creative/blueprint",
  "/creative/comic",
  "/creative/vaporwave",
  "/creative/museum",
  "/creative/library",
  "/creative/pixel",
  "/creative/noir",
  "/creative/steampunk",
  "/creative/scroll",
  "/creative/tv",
] as const;

// Back button + reroll for returning to mode selector
export const BackButton: React.FC<{ className?: string }> = ({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const reroll = () => {
    const others = CREATIVE_VARIANTS.filter((v) => v !== location.pathname);
    const pick = others[Math.floor(Math.random() * others.length)];
    navigate(pick);
  };

  const linkStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    textDecoration: "none",
    color: "inherit",
    opacity: 0.7,
    transition: "opacity 0.2s",
    fontSize: "0.9rem",
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 }}
      style={{
        position: "fixed",
        top: "1.5rem",
        left: "1.5rem",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
      }}
    >
      <Link
        to="/"
        className={className}
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
      >
        <span>←</span>
        <span>Back</span>
      </Link>
      <button
        onClick={reroll}
        title="Random variant"
        style={{
          background: "none",
          border: "1px solid currentColor",
          borderRadius: "50%",
          width: "28px",
          height: "28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "inherit",
          opacity: 0.5,
          transition: "opacity 0.2s, transform 0.2s",
          fontSize: "0.85rem",
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "1";
          e.currentTarget.style.transform = "rotate(180deg)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "0.5";
          e.currentTarget.style.transform = "rotate(0deg)";
        }}
      >
        ⟳
      </button>
    </motion.div>
  );
};

// Lazy loading image component
export const LazyImage: React.FC<{
  src: string;
  alt: string;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}> = ({ src, alt, onClick, className, style }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(imgRef, { once: true, margin: "100px" });

  return (
    <div ref={imgRef} className={className} onClick={onClick} style={{ cursor: onClick ? "pointer" : undefined, ...style }}>
      {isInView && (
        <>
          {!isLoaded && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: 120, opacity: 0.3 }}>
              Loading...
            </div>
          )}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            style={{ opacity: isLoaded ? 1 : 0, width: "100%", height: "100%", objectFit: "cover", transition: "opacity 0.3s" }}
          />
        </>
      )}
      {!isInView && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", minHeight: 120, opacity: 0.3 }}>
          ◇
        </div>
      )}
    </div>
  );
};

// Fullscreen Image Viewer
export const ImageViewer: React.FC<{
  images: { src: string; title: string }[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}> = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  return (
    <motion.div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        cursor: "pointer",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: "1rem", right: "1rem",
          background: "none", border: "none", color: "white",
          fontSize: "2rem", cursor: "pointer", zIndex: 1,
        }}
      >
        ×
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        style={{
          position: "absolute", left: "1rem", top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", color: "white",
          fontSize: "3rem", cursor: "pointer", zIndex: 1,
        }}
      >
        ‹
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        style={{
          position: "absolute", right: "1rem", top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", color: "white",
          fontSize: "3rem", cursor: "pointer", zIndex: 1,
        }}
      >
        ›
      </button>
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        style={{ maxWidth: "90vw", maxHeight: "85vh", textAlign: "center" }}
      >
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].title}
          style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
        />
        <p style={{ color: "white", marginTop: "1rem", fontSize: "0.9rem" }}>
          {images[currentIndex].title}
        </p>
      </motion.div>
    </motion.div>
  );
};

// Lazy YouTube embed
export const LazyYouTubeEmbed: React.FC<{
  videoId: string;
  title: string;
  className?: string;
}> = ({ videoId, title, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "200px" });

  return (
    <div ref={ref} className={className} style={{ aspectRatio: "16/9", background: "#111" }}>
      {isInView ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#555" }}>
          ▶ {title}
        </div>
      )}
    </div>
  );
};

// Painting gallery with viewer
export const usePaintingViewer = (paintings: { src: string; title: string }[]) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openViewer = (index: number) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % paintings.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + paintings.length) % paintings.length);
  const closeViewer = () => setViewerOpen(false);

  const ViewerPortal = () => (
    <AnimatePresence>
      {viewerOpen && (
        <ImageViewer
          images={paintings}
          currentIndex={currentIndex}
          onClose={closeViewer}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </AnimatePresence>
  );

  return { openViewer, ViewerPortal };
};

// Footer
export const CreativeFooter: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <footer className={className} style={{ textAlign: "center", padding: "3rem 1rem", opacity: 0.6, ...style }}>
    <Link to="/ai-philosophy" style={{ color: "inherit", textDecoration: "none", display: "block", marginBottom: "0.5rem" }}>
      My thoughts on AI & Creativity
    </Link>
    <span style={{ fontSize: "0.8rem" }}>mbehery@protonmail.com</span>
  </footer>
);
